<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useHead } from '@unhead/vue';
import { useMediaQuery } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { getHomeHeroImages, type GetHomeHeroImagesProjectResponse } from '@/api';
import SearchBox from '@/views/search/SearchBox.vue';
import HomeQuickLinks from '@/views/home/HomeQuickLinks.vue';
import { createHomeHeroImagesPayload, createHomeHeroRotationScript, HomeHeroImageElementId, takeHomeHeroScriptState, type HomeHeroScriptImage } from '@/views/home/hero-image-rotation.ts';
import { Constants } from '@/utilities/constants.ts';
import { useSsgState } from '@/utilities/ssg-state.ts';
import { useTracking } from '@/utilities/tracking.ts';

type HeroImage = { id: number; image: string; project: GetHomeHeroImagesProjectResponse | null };

const DefaultHeroImageCount = 10;

const { t } = useI18n();
const { trackAction, trackImpression } = useTracking();

const pickRandom = <T,>(items: T[]): T | null => (items.length > 0 ? (items[Math.floor(Math.random() * items.length)] ?? null) : null);

const defaultHeroImages: HeroImage[] = Array.from({ length: DefaultHeroImageCount }, (_, index) => ({
  id: -(index + 1),
  image: `${Constants.CdnBaseUrl}/public/hero${String(index + 1).padStart(2, '0')}.webp`,
  project: null,
}));

const defaultHeroImage = (): HeroImage => pickRandom(defaultHeroImages) ?? { id: -1, image: `${Constants.CdnBaseUrl}/public/hero01.webp`, project: null };

const heroImages = ref<HeroImage[] | null>(null);
const heroImage = ref<HeroImage>(defaultHeroImage());
const mounted = ref(false);
const isNarrowScreen = useMediaQuery('(max-width: 639px)');
const searchPlaceholder = computed(() => (mounted.value && isNarrowScreen.value ? t('views.search.placeholder-short') : undefined));

const candidateHeroImages = computed(() => (heroImages.value && heroImages.value.length > 0 ? heroImages.value : defaultHeroImages));

const fetchHeroImages = async (): Promise<HeroImage[] | null> => {
  const result = await getHomeHeroImages();
  if (result.response?.ok && result.data) {
    return result.data.items.map(({ id, image, project }) => ({ id, image, project }));
  }

  console.error(`Fetching home hero images failed: ${result.error}`);
  return null;
};

const loadHeroImages = async () => {
  heroImages.value = (await fetchHeroImages()) ?? [];
  heroImage.value = pickRandom(candidateHeroImages.value) ?? heroImage.value;
};

const refreshHeroImage = async () => {
  if (!heroImage.value.project) {
    return;
  }

  const activeHeroImages = await fetchHeroImages();
  if (activeHeroImages === null) {
    return;
  }

  heroImages.value = activeHeroImages;
  heroImage.value = activeHeroImages.find((x) => x.id === heroImage.value.id) ?? pickRandom(activeHeroImages) ?? defaultHeroImage();
};

// La photo est tirée au hasard à chaque visite. Une page pré-rendue embarque la liste des photos et un script d'en-tête
// qui en choisit une avant le chargement de l'image, pour ne jamais remplacer une image déjà affichée ; sinon le tirage
// se fait dans le navigateur quand la liste est chargée. Le serveur du site tient la liste du script à jour sans attendre
// le rebuild de nuit, donc elle remplace celle de l'état pré-rendu.
const { hydrated, ready } = useSsgState({
  key: 'home-hero-images',
  refs: { heroImages, heroImage },
  isLoaded: () => heroImages.value !== null,
  load: loadHeroImages,
});

const scriptState = !import.meta.env.SSR && hydrated ? takeHomeHeroScriptState() : null;
if (scriptState) {
  heroImages.value = scriptState.images;
}
const scriptHeroImage = scriptState ? (candidateHeroImages.value.find((x) => x.id === scriptState.id) ?? null) : null;
if (!import.meta.env.SSR && hydrated) {
  heroImage.value = scriptHeroImage ?? pickRandom(candidateHeroImages.value) ?? heroImage.value;
}

const imageRendered = computed(() => mounted.value || scriptHeroImage !== null || (!import.meta.env.SSR && !hydrated));

if (import.meta.env.SSR) {
  useHead({
    script: computed(() => [
      {
        key: 'home-hero-image-rotation',
        'data-home-hero-images': createHomeHeroImagesPayload((heroImages.value ?? []).filter((x): x is HomeHeroScriptImage => x.project !== null)),
        innerHTML: createHomeHeroRotationScript(defaultHeroImages),
      },
    ]),
  });
}

onMounted(async () => {
  mounted.value = true;
  await ready();
  if (hydrated) {
    await refreshHeroImage();
  }
  if (heroImage.value.project) {
    void trackImpression('project', heroImage.value.project.id, 'homeHeroImpression');
  }
});

const trackProjectImageClick = (projectId: number) => {
  void trackAction('project', projectId, 'homeHeroClick');
};
</script>

<template>
  <div class="relative aspect-[5/2] bg-gray-300">
    <!-- Background image layer -->
    <img
      :id="HomeHeroImageElementId"
      sizes="100vw"
      :srcset="imageRendered ? heroImage.image.toSrcset(true) : undefined"
      :src="imageRendered ? heroImage.image.resizedToSmall(true) : undefined"
      alt=""
      aria-hidden="true"
      class="absolute inset-0 h-full w-full object-cover"
      fetchpriority="high"
    />

    <!-- Content layer -->
    <div class="relative mx-auto flex h-full max-w-7xl items-start pt-3 sm:items-center sm:pt-0">
      <div class="mx-3 grow sm:mx-auto sm:w-2/3 sm:grow-0">
        <SearchBox :placeholder="searchPlaceholder" />
      </div>
    </div>

    <RouterLink
      v-if="mounted && heroImage.project"
      :aria-label="t('views.home.hero.project-image', { name: heroImage.project.name })"
      :to="{ name: 'project', params: { slug: heroImage.project.name.toSlug(), projectId: heroImage.project.id } }"
      @click="trackProjectImageClick(heroImage.project.id)"
      class="absolute right-2 bottom-2 z-10 flex h-7 max-w-[calc(100%-1rem)] items-stretch rounded-md bg-black/60 text-white before:absolute before:-inset-2 before:content-[''] hover:bg-black/80 sm:h-10 lg:bottom-10"
    >
      <span class="flex min-w-0 items-center px-2.5 sm:px-3.5">
        <span class="truncate text-xs font-semibold sm:text-sm">{{ heroImage.project.name }}</span>
      </span>
    </RouterLink>
  </div>
  <HomeQuickLinks />
</template>
